import { Component, NgZone, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import * as polyline from '@mapbox/polyline';
import { TranslateService } from '@ngx-translate/core';
import { Layer } from 'leaflet';
import 'leaflet';
import 'leaflet-extra-markers';
import 'leaflet-routing-machine';

import { DetailsBase } from 'app/locations/details/detailsbase';
import { AlertcLinear, Siterecord, VildVersion } from 'app/models';
import { ConfigurationStatusType, FcdEncodingStatusType, VildEncodingStatusType } from 'app/models/enums';
import { Fcd, SubTrajectorySegment } from 'app/models/fcd';
import { ShapeStyle } from 'app/models/shapestyle.model';
import {
  AuthorizationService,
  ErrorService,
  MapService,
  SiterecordCommentService,
  SiterecordService,
  StorageService,
  VildVersionService
} from 'app/services';
import { DialogConfirmationComponent } from 'app/shared/dialog-delete-location/dialog-confirmation.component';
import { DialogEditLocationIdComponent } from 'app/shared/dialog-edit-location-id/dialog-edit-location-id.component';
import { LinearDetailsGeneralComponent } from 'app/shared/linear-details/general/linear-details-general.component';
import { LinearDetailsVildComponent } from 'app/shared/linear-details/vild/linear-details-vild.component';

import { DialogChangeOriginalLinearComponent } from './change-original-linear/dialog-change-original-linear.component';

declare const L: any;

@Component({
  selector: 'app-linear-details',
  templateUrl: './linear-details.component.html',
  styleUrls: ['./linear-details.component.scss']
})
export class LinearDetailsComponent extends DetailsBase implements OnInit {
  @ViewChildren(LinearDetailsVildComponent) linearDetailsVild: QueryList<LinearDetailsVildComponent>;
  @ViewChild(LinearDetailsGeneralComponent) linearDetailsGeneral: LinearDetailsGeneralComponent;

  @ViewChild('dialogEditLocationIdComponent') dialogEditLocationIdComponent: DialogEditLocationIdComponent;
  @ViewChild('confirmDeleteRollbackDialog') dialogConfirmationComponent: DialogConfirmationComponent;
  @ViewChild('changeOriginalLinearDialog') dialogChangeOriginalLinearComponent: DialogChangeOriginalLinearComponent;

  public fcdData: Fcd;

  private originalShapeLayer: L.Layer;
  private fcdShapeLayer: L.Layer;
  private fcdSegmentLayer: L.Layer;
  private originalShapeTranslation: string;
  private fcdShapeTranslation: string;
  private fcdSegmentTranslation: string;

  constructor(
    route: ActivatedRoute,
    router: Router,
    authorizationService: AuthorizationService,
    siterecordService: SiterecordService,
    siterecordCommentService: SiterecordCommentService,
    storageService: StorageService,
    translateService: TranslateService,
    mapService: MapService,
    vildVersionService: VildVersionService,
    private readonly errorService: ErrorService,
    private readonly ngZone: NgZone
  ) {
    super(
      route,
      router,
      authorizationService,
      siterecordService,
      siterecordCommentService,
      storageService,
      translateService,
      mapService,
      vildVersionService
    );
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.errorService.linearDetailsComponent = this;
  }

  protected initializeControl(draggable: boolean): void {
    if (this.control) {
      this.map.removeControl(this.control);
    }

    this.control = L.Routing.control({
      createMarker: (i, dStart, n) => this.placeMarkers(i, dStart, n, draggable),
      waypointMode: 'snap',
      routeWhileDragging: false,
      fitSelectedRoutes: true,
      autoRoute: false,
      router: new L.Routing.PgRouting('cbmdb', { serviceUrl: this.mapService.geoserverWfsUrl })
    }).addTo(this.map);

    this.control.on('routeselected', e => {
      const waypoints = this.control.getWaypoints();
      let routeFound = true;

      if (waypoints[0].latLng === null || waypoints[waypoints.length - 1].latLng === null) {
        routeFound = false;
      }

      if (routeFound) {
        this.previousWaypoints = [];
        for (const waypoint of waypoints) {
          this.previousWaypoints.push(L.latLng(waypoint.latLng.lat, waypoint.latLng.lng));
        }
      } else if (!routeFound) {
        this.errorFindingRoute();
      }
    });

    this.control.on('routingerror', e => this.errorFindingRoute());
  }

  private drawLines(): void {
    if (this.location.lineShape) {
      this.map.createPane(this.SHAPE_STYLE.pane);
      this.map.getPane(this.SHAPE_STYLE.pane).style.zIndex = '230';
      this.shapeLayer = this.createLine(this.location.lineShape, this.SHAPE_STYLE);
      this.featureGroup.addLayer(this.shapeLayer);
      this.layerControl.addOverlay(this.shapeLayer, this.shapeTranslation);
    }

    if (this.location.previousLineShape) {
      this.map.createPane(this.PREVIOUS_SHAPE_STYLE.pane);
      this.map.getPane(this.PREVIOUS_SHAPE_STYLE.pane).style.zIndex = '225';
      this.previousShapeLayer = this.createLine(this.location.previousLineShape, this.PREVIOUS_SHAPE_STYLE);
      this.featureGroup.addLayer(this.previousShapeLayer);
      this.layerControl.addOverlay(this.previousShapeLayer, this.previousShapeTranslation);
    }

    if (this.location.originalLineShape) {
      this.map.createPane(this.ORIGINAL_SHAPE_STYLE.pane);
      this.map.getPane(this.ORIGINAL_SHAPE_STYLE.pane).style.zIndex = '220';
      this.originalShapeLayer = this.createLine(this.location.originalLineShape, this.ORIGINAL_SHAPE_STYLE);
      this.featureGroup.addLayer(this.originalShapeLayer);
      this.layerControl.addOverlay(this.originalShapeLayer, this.originalShapeTranslation);
    }

    if (this.isLocationAdmin()) {
      if (this.fcdData && this.fcdData.fcdMatchGeometry) {
        this.map.createPane(this.FCD_SHAPE_STYLE.pane);
        this.map.getPane(this.FCD_SHAPE_STYLE.pane).style.zIndex = '215';
        this.fcdShapeLayer = this.createFcdShapeLayer();
        this.featureGroup.addLayer(this.fcdShapeLayer);
        this.layerControl.addOverlay(this.fcdShapeLayer, this.fcdShapeTranslation);
      }

      this.resetFcdSegmentLayer();
      if (this.isContractTypeFCD()) {
        this.addFcdSegmentLayerToMap();
      }
    }

    this.featureGroup.addTo(this.map);
    this.map.fitBounds(this.featureGroup.getBounds());
  }

  private createFcdShapeLayer(): Layer | undefined {
    if (!this.fcdData.fcdMatchGeometry) {
      return undefined;
    }

    if (typeof this.fcdData.fcdMatchGeometry === 'string') {
      return this.decodeJtsLine(this.fcdData.fcdMatchGeometry, this.FCD_SHAPE_STYLE);
    }

    if (this.fcdData.fcdMatchGeometry.type === 'LineString') {
      const coordinates: [number, number][] = this.fcdData.fcdMatchGeometry.coordinates
        .map(point => point as [number, number]);
      return this.createPolyline(coordinates, this.FCD_SHAPE_STYLE);
    }
  }

  private resetFcdSegmentLayer() {
    if (this.fcdSegmentLayer) {
      // Switching location with next and previous should reset the fcd segment layer.
      // This prevents duplicate layers and incorrect display of the segment layer.
      this.layerControl.removeLayer(this.fcdSegmentLayer);
      this.fcdSegmentLayer.removeFrom(this.map);
    }
  }

  private addFcdSegmentLayerToMap() {
    this.fcdSegmentLayer = L.tileLayer(this.mapService.fcdSegmentsTilesUrl, {
      minZoom: 4,
      maxZoom: 20
    });
    this.layerControl.addOverlay(this.fcdSegmentLayer, this.fcdSegmentTranslation);
  }

  private createLine(shape: string[], shapeStyle: ShapeStyle): L.Polyline {
    const linestringRegexp = /\((.*?)\)/g;
    const lineShape = shape.toString();
    const points = linestringRegexp.exec(lineShape)[1].split(', ');
    const pointList = points.map(point => {
      const lngLat = point.split(' ');
      return [parseFloat(lngLat[1]), parseFloat(lngLat[0])];
    });

    return new L.polyline(pointList, {
      color: shapeStyle.lineColor,
      weight: shapeStyle.lineWeight,
      opacity: 0.5,
      pane: shapeStyle.pane
    });
  }

  private decodeJtsLine(lineString: string, shapeStyle: ShapeStyle) {
    const fcdLineString: [number, number][] = polyline.decode(lineString);
    const pointList = fcdLineString.map(point => [point[0], point[1]]);

    return new L.polyline(pointList, {
      color: shapeStyle.lineColor,
      weight: shapeStyle.lineWeight,
      opacity: 0.5,
      pane: shapeStyle.pane
    });
  }

  private createPolyline(coordinates: [number, number][], shapeStyle: ShapeStyle) {
    return new L.polyline(coordinates.map(point => [point[1], point[0]]), {
      color: shapeStyle.lineColor,
      weight: shapeStyle.lineWeight,
      opacity: 0.5,
      pane: shapeStyle.pane
    });
  }

  private placeMarkers(i: number, dStart: any, n: number, draggable: boolean): void {
    const marker = L.marker(dStart.latLng, {
      draggable,
      icon: i === 0 ? this.greenMarker : i === n - 1 ? this.redMarker : this.blueMarker
    });

    if (!this.readonly && this.isAllowedToEditRoute()) {
      if (i !== 0 && i !== n - 1) {
        const container = L.DomUtil.create('div');
        const deleteButton = L.DomUtil.create('button', '', container);
        deleteButton.setAttribute('type', 'button');
        deleteButton.setAttribute('class', 'btn btn-danger btn-xs');

        deleteButton.innerHTML = this.translateService.instant('BUTTON.DELETE');

        marker.bindPopup(container);

        L.DomEvent.on(deleteButton, 'click', e => {
          this.control.spliceWaypoints(i, 1);
          this.map.closePopup();
        });
      }
    }

    this.featureGroup.addLayer(marker);
    return marker;
  }

  private setWaypoints(): void {
    this.control.setWaypoints(
      this.location.routingPoints.split('|').map(rp => {
        const lngLat = rp.replace('\\', '').split(',');
        return L.latLng(lngLat[1], lngLat[0]);
      })
    );
  }

  protected getLocationById(locationId: number): void {
    this.isLoading = true;
    this.siterecordService.getSiterecordById(locationId).subscribe(siterecord => {
      this.location = siterecord;
      this.originalLocation = JSON.stringify(siterecord);
      if (!this.isContractDataproviderTypeMst()) {
        if (this.location.currentAlertcLinears.length === 0) {
          this.location.currentAlertcLinears.push(this.initializeAlertcLinear(this.currentVildVersion));
          this.isCurrentAlertcAdded = true;
        }
        if (this.futureVildVersion) {
          if (this.location.futureAlertcLinears.length === 0) {
            this.location.futureAlertcLinears.push(this.initializeAlertcLinear(this.futureVildVersion));
            this.isFutureAlertcAdded = true;
          }
        }
      }
      if (this.location.openlrStructure) {
        this.openlr = JSON.parse(this.location.openlrStructure.openlr).openlrExtendedLinear;
      }
      if (this.location.fcdStructure) {
        this.fcdData = JSON.parse(this.location.fcdStructure.fcd);
      }
      this.initializeReadonly();
      if (this.readonly) {
        this.setWaypoints();
      }
      this.drawLines();
      this.isLoading = false;
    });
  }

  public isFcdReadonly(): boolean {
    return this.readonly && this.fcdData && this.isLocationAdmin();
  }

  public onCancel(): void {
    if (this.isEditPage) {
      this.goBackToOverview();
    } else {
      this.isCanceling = true;
      this.errorMessages = [];
      this.location = JSON.parse(this.originalLocation);
      if (this.location.fcdStructure) {
        this.fcdData = JSON.parse(this.location.fcdStructure.fcd);
      }
      this.readonly = true;

      if (this.readonly && this.isAllowedToEditRoute()) {
        this.initializeControl(false);
        this.setWaypoints();
        this.map.fitBounds(this.featureGroup.getBounds());
      }
      this.isCanceling = false;
    }
  }

  protected onEdit(): void {
    if (this.isAllowedToEditRoute()) {
      this.initializeControl(!this.readonly);
      this.setWaypoints();

      if (!this.readonly) {
        this.ngZone.runGuarded(() => this.control.route());
        if (this.control.line) {
          this.featureGroup.addLayer(this.control.line);
        }
        this.map.fitBounds(this.featureGroup.getBounds());

        this.control.on('waypointschanged', e => {
          this.ngZone.runGuarded(() => this.control.route());
          this.location.routingPoints = this.control
            .getWaypoints()
            .map(waypoint => `${waypoint.latLng.lng}\\,${waypoint.latLng.lat}`)
            .join('|');
        });
      }
    }
  }

  private setAlertcLinears(copyOfLocation: Siterecord): void {
    const newAlertcLinearsArray = [];
    this.linearDetailsVild.forEach(ldv => newAlertcLinearsArray.push(ldv.exportLinearsVildComponent()));

    const newAlertcLinears = [];
    newAlertcLinearsArray.forEach(nala => nala.forEach(element => newAlertcLinears.push(element)));
    copyOfLocation.alertcLinears = newAlertcLinears;
  }

  private setStatusManual(alertcLinears: AlertcLinear[]) {
    alertcLinears.forEach(acl => {
      acl.vildEncodingStatus = VildEncodingStatusType.MANUAL;
    });
  }

  private vildChanges(copyOfLocation: Siterecord): void {
    const orLocation = JSON.parse(this.originalLocation);

    this.sortAlertcLinears(copyOfLocation.currentAlertcLinears);
    this.sortAlertcLinears(orLocation.currentAlertcLinears);
    let hasDifferences = this.getDifferences(copyOfLocation.currentAlertcLinears, orLocation.currentAlertcLinears);
    if (hasDifferences) {
      this.setStatusManual(copyOfLocation.currentAlertcLinears);
    }

    this.sortAlertcLinears(copyOfLocation.futureAlertcLinears);
    this.sortAlertcLinears(orLocation.futureAlertcLinears);
    hasDifferences = this.getDifferences(copyOfLocation.futureAlertcLinears, orLocation.futureAlertcLinears);
    if (hasDifferences) {
      this.setStatusManual(copyOfLocation.futureAlertcLinears);
    }

    this.sortAlertcLinears(copyOfLocation.alertcLinears);
    this.sortAlertcLinears(orLocation.alertcLinears);
    hasDifferences = this.getDifferences(copyOfLocation.alertcLinears, orLocation.alertcLinears);
    if (hasDifferences) {
      this.setStatusManual(copyOfLocation.alertcLinears);
    }
  }

  private getDifferences(newAlertc: AlertcLinear[], originalAlertc: AlertcLinear[]): boolean {
    if (newAlertc.length !== originalAlertc.length) {
      return true;
    } else {
      for (let i = 0; i < newAlertc.length; i++) {
        if (
          newAlertc[i].alertcIndex !== originalAlertc[i].alertcIndex ||
          newAlertc[i].directionCoded !== originalAlertc[i].directionCoded ||
          newAlertc[i].primaryOffsetDistance !== originalAlertc[i].primaryOffsetDistance ||
          newAlertc[i].secondaryOffsetDistance !== originalAlertc[i].secondaryOffsetDistance ||
          newAlertc[i].startLatitude !== originalAlertc[i].startLatitude ||
          newAlertc[i].startLongitude !== originalAlertc[i].startLongitude ||
          newAlertc[i].endLatitude !== originalAlertc[i].endLatitude ||
          newAlertc[i].endLongitude !== originalAlertc[i].endLongitude ||
          newAlertc[i].carriagewayStartpoint !== originalAlertc[i].carriagewayStartpoint ||
          newAlertc[i].carriagewayEndpoint !== originalAlertc[i].carriagewayEndpoint ||
          newAlertc[i].lengthAffected !== originalAlertc[i].lengthAffected ||
          (newAlertc[i].primaryVildLocation &&
            newAlertc[i].primaryVildLocation.locNr &&
            (originalAlertc[i].primaryVildLocation === null ||
              newAlertc[i].primaryVildLocation.locNr !== originalAlertc[i].primaryVildLocation.locNr)) ||
          (newAlertc[i].secondaryVildLocation &&
            newAlertc[i].secondaryVildLocation.locNr &&
            (originalAlertc[i].secondaryVildLocation === null ||
              newAlertc[i].secondaryVildLocation.locNr !== originalAlertc[i].secondaryVildLocation.locNr))
        ) {
          return true;
        }
      }
    }
    return false;
  }

  private sortAlertcLinears(alertcLinears: AlertcLinear[]): void {
    alertcLinears.sort((alertcA, alertcB) => {
      const indexA = alertcA.alertcIndex;
      const indexB = alertcB.alertcIndex;
      return indexA < indexB ? -1 : indexA > indexB ? 1 : 0;
    });
  }

  private hasAlertcValues(alertc: AlertcLinear[]): boolean {
    if (alertc.length > 1) {
      return true;
    }
    for (const a of alertc) {
      if (
        a.alertcIndex ||
        a.directionCoded ||
        a.primaryOffsetDistance ||
        a.secondaryOffsetDistance ||
        a.startLatitude ||
        a.startLongitude ||
        a.endLatitude ||
        a.endLongitude ||
        a.carriagewayStartpoint ||
        a.carriagewayEndpoint ||
        a.lengthAffected ||
        (a.primaryVildLocation && a.primaryVildLocation.locNr) ||
        (a.secondaryVildLocation && a.secondaryVildLocation.locNr)
      ) {
        return true;
      }
    }
    return false;
  }

  private getFcdData(copyOfLocation: Siterecord): void {
    const orgJson: Fcd = JSON.parse(this.location.fcdStructure.fcd);

    if (this.fcdData.trajectoryFreeFlowSpeed !== orgJson.trajectoryFreeFlowSpeed) {
      copyOfLocation.fcdEncodingStatus = FcdEncodingStatusType.MANUAL;
    }

    if (!orgJson.subTrajectorySegments[0] && this.fcdData.subTrajectorySegments.length === 1) {
      if (!this.hasFcdSubTrajectorySegmentValues(this.fcdData.subTrajectorySegments[0])) {
        this.fcdData.subTrajectorySegments = orgJson.subTrajectorySegments;
      }
    } else {
      for (let i = 0, j = 0; i < this.fcdData.subTrajectorySegments.length; i++) {
        const newSts = this.fcdData.subTrajectorySegments[i];
        const oldSts = orgJson.subTrajectorySegments[j];

        if (!newSts.manual) {
          if (JSON.stringify(newSts) !== JSON.stringify(oldSts)) {
            if (oldSts.fcdSegments.length === 0 && !this.hasFcdErrorsChanged(newSts, oldSts)) {
              newSts.fcdSegments = oldSts.fcdSegments;
              j++;
            } else {
              const currentJ = j;
              for (let x = j; x < orgJson.subTrajectorySegments.length; x++) {
                if (JSON.stringify(newSts) === JSON.stringify(orgJson.subTrajectorySegments[x])) {
                  j = x + 1;
                }
              }
              if (currentJ === j) {
                newSts.manual = true;
                j++;
              }
            }
          } else if (JSON.stringify(newSts) === JSON.stringify(oldSts)) {
            j++;
          }
        }
      }
    }
  }

  private hasFcdSubTrajectorySegmentValues(sts: SubTrajectorySegment): boolean {
    if (
      (sts.fcdSegments.length === 1 &&
        sts.fcdSegments[0] &&
        (sts.fcdSegments[0].fcdBeginFraction ||
          sts.fcdSegments[0].fcdEndFraction ||
          sts.fcdSegments[0].fcdFingerprintSegmentID ||
          sts.fcdSegments[0].fcdInternalSegmentID ||
          sts.fcdSegments[0].fcdLength ||
          sts.fcdSegments[0].fcdOriginalSegmentID ||
          sts.fcdSegments[0].fcdSegmentFreeFlowSpeed ||
          sts.fcdSegments[0].fcdSubtrajectorySpecificity ||
          sts.fcdSegments[0].fcdTrajectoryCoverage ||
          sts.fcdSegments[0].fcdTrajectorySpecificity)) ||
      sts.mstFractionBegin ||
      sts.mstFractionEnd
    ) {
      return true;
    }
    return false;
  }

  private hasFcdErrorsChanged(newSts: SubTrajectorySegment, oldSts: SubTrajectorySegment): boolean {
    if (
      newSts.mstFractionBegin !== oldSts.mstFractionBegin ||
      newSts.mstFractionEnd !== oldSts.mstFractionEnd ||
      (newSts.fcdSegments.length === 1 &&
        oldSts.fcdSegments.length === 0 &&
        (newSts.fcdSegments[0].fcdBeginFraction ||
          newSts.fcdSegments[0].fcdEndFraction ||
          newSts.fcdSegments[0].fcdFingerprintSegmentID ||
          newSts.fcdSegments[0].fcdInternalSegmentID ||
          newSts.fcdSegments[0].fcdLength ||
          newSts.fcdSegments[0].fcdOriginalSegmentID ||
          newSts.fcdSegments[0].fcdSegmentFreeFlowSpeed ||
          newSts.fcdSegments[0].fcdSubtrajectorySpecificity ||
          newSts.fcdSegments[0].fcdTrajectoryCoverage ||
          newSts.fcdSegments[0].fcdTrajectorySpecificity))
    ) {
      return true;
    }
    return false;
  }

  public async onSaveAndApprove(status: ConfigurationStatusType) {
    this.isSaving = true;
    this.errorMessages = [];

    const copyOfLocation = JSON.parse(JSON.stringify(this.location));
    this.setSiterecordParts(copyOfLocation);
    this.updateSiterecordAndStatus(copyOfLocation, status);
  }

  public onSave(): void {
    this.isSaving = true;
    this.errorMessages = [];

    const copyOfLocation = JSON.parse(JSON.stringify(this.location));
    this.setSiterecordParts(copyOfLocation);
    this.updateSiterecord(copyOfLocation);
  }

  public setSiterecordParts(copyOfLocation: any): void {
    if (!this.isLocationAdmin()) {
      console.warn('setSiterecordParts skipped because the user is not an administrator');
      return;
    }

    if (this.isCurrentAlertcAdded) {
      copyOfLocation.currentAlertcLinears = this.linearDetailsVild.first.exportLinearsVildComponent();
      const hasDifferences = this.hasAlertcValues(copyOfLocation.currentAlertcLinears);
      if (!hasDifferences) {
        copyOfLocation.currentAlertcLinears.splice(0, 1);
      }
    }

    if (this.isFutureAlertcAdded) {
      copyOfLocation.futureAlertcLinears[0] = this.linearDetailsVild.last.exportLinearsVildComponent();
      const hasDifferences = this.hasAlertcValues(copyOfLocation.futureAlertcLinears);
      if (!hasDifferences) {
        copyOfLocation.futureAlertcLinears.splice(0, 1);
      }
    }

    this.setAlertcLinears(copyOfLocation);
    this.vildChanges(copyOfLocation);

    if (this.location.fcdStructure) {
      this.getFcdData(copyOfLocation);
      copyOfLocation.fcdStructure.fcd = JSON.stringify(this.fcdData);
    }
  }

  protected removeLayers(): void {
    if (this.shapeLayer) {
      this.layerControl.removeLayer(this.shapeLayer);
    }
    if (this.previousShapeLayer) {
      this.layerControl.removeLayer(this.previousShapeLayer);
    }
    if (this.originalShapeLayer) {
      this.layerControl.removeLayer(this.originalShapeLayer);
    }
    if (this.fcdShapeLayer) {
      this.layerControl.removeLayer(this.fcdShapeLayer);
    }
  }

  protected initializeLegend(): void {
    const labels = [
      this.newShapeTranslation,
      this.shapeTranslation,
      this.previousShapeTranslation,
      this.originalShapeTranslation
    ];

    const colors = [
      this.NEW_SHAPE_STYLE.lineColor,
      this.SHAPE_STYLE.lineColor,
      this.PREVIOUS_SHAPE_STYLE.lineColor,
      this.ORIGINAL_SHAPE_STYLE.lineColor
    ];
    if (this.isLocationAdmin()) {
      labels.push(this.fcdShapeTranslation);
      colors.push(this.FCD_SHAPE_STYLE.lineColor);

      labels.push(this.fcdSegmentTranslation);
      colors.push('rgba(210, 210, 230, 0.8)');
    }
    this.createLegend(labels, colors);
  }

  protected initializeTranslations(): void {
    this.translate('LOCATIONS.LEGEND.NEWLINEAR').subscribe(translation => (this.newShapeTranslation = translation));

    this.translate('LOCATIONS.LEGEND.CURRENTLINEAR').subscribe(translation => (this.shapeTranslation = translation));

    this.translate('LOCATIONS.LEGEND.PREVIOUSLINEAR').subscribe(
      translation => (this.previousShapeTranslation = translation)
    );

    this.translate('LOCATIONS.LEGEND.ORIGINALLINEAR').subscribe(
      translation => (this.originalShapeTranslation = translation)
    );

    this.translate('LOCATIONS.LEGEND.FCDLINEAR').subscribe(translation => (this.fcdShapeTranslation = translation));
    this.translate('LOCATIONS.LEGEND.FCDSEGMENT').subscribe(translation => (this.fcdSegmentTranslation = translation));

    this.translate('ERROR.FE001').subscribe(translation => (this.noRouteFoundTranslation = translation));

    this.translate('ERROR.FE002').subscribe(translation => (this.failedSaveTranslation = translation));
  }

  private initializeAlertcLinear(vildVersion: VildVersion): AlertcLinear {
    const linear = new AlertcLinear();
    linear.alertcIndex = 0;
    linear.siterecordId = this.location.id;
    linear.vildVersion = vildVersion;
    linear.vildEncodingStatus = VildEncodingStatusType.ERROR;
    linear.locationCountryCode = '8';
    return linear;
  }

  public showEditLocationIdModal() {
    this.dialogEditLocationIdComponent.show();
  }

  public showRollbackDeletedModal() {
    this.dialogConfirmationComponent.show();
  }

  openModalToChangeTheOriginalLinear() {
    this.dialogChangeOriginalLinearComponent.show();
  }

  changeUseCurrentLinearAsOriginalLinear() {
    const copyOfLocation = JSON.parse(this.originalLocation);
    copyOfLocation.originalLineShape = [...this.location.lineShape];

    this.updateSiterecord(copyOfLocation);
  }
}



// WEBPACK FOOTER //
// ./src/app/locations/details/linear/linear-details.component.ts